/**
 * Catalog List Screen
 * Manage catalog items (products/menu items) for the shop
 * Full CRUD operations with shop theming
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Modal, Alert, ActivityIndicator, Image } from 'react-native';
import { useAuthTokens, useCurrentShop, getConfig, useThemeColors, useThemeAccent, configurationService, type Category, type UnitOfMeasurement, type Country } from '@app/core';
import { Button, Input, Card, Text } from '@app/design-system';
import { colors } from '@app/design-system';
import { ShopHeader } from '@modules/shop-shell';
import { storageService, type UploadProgress } from '@app/core';

/**
 * Hook to lazy load catalog image from blobPath
 */
const useCatalogImage = (blobPath: string | undefined | null) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      if (!blobPath) {
        setImageUrl(null);
        return;
      }

      // If it's already a full URL (backward compatibility), use it directly
      if (blobPath.startsWith('http://') || blobPath.startsWith('https://')) {
        setImageUrl(blobPath);
        return;
      }

      // Otherwise, get read URL from backend
      setLoading(true);
      setError(null);

      try {
        console.log('🖼️ [Image] Loading read URL for blobPath:', blobPath);
        const readUrl = await storageService.getReadUrl(blobPath);

        if (isMounted) {
          console.log('✅ [Image] Read URL loaded successfully');
          setImageUrl(readUrl);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error('❌ [Image] Failed to load read URL:', err);
          setError(err instanceof Error ? err.message : 'Failed to load image');
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [blobPath]);

  return { imageUrl, loading, error };
};

/**
 * CatalogImage Component - Lazy loads image from blobPath
 */
interface CatalogImageProps {
  blobPath: string | undefined | null;
  style?: any;
}

const CatalogImage: React.FC<CatalogImageProps> = ({ blobPath, style }) => {
  const { imageUrl, loading, error } = useCatalogImage(blobPath);

  if (loading) {
    return (
      <View style={[style, { justifyContent: 'center', alignItems: 'center', backgroundColor: '#f0f0f0' }]}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  }

  if (error || !imageUrl) {
    return (
      <View style={[style, { justifyContent: 'center', alignItems: 'center', backgroundColor: '#f0f0f0' }]}>
        <Text variant="caption" color="secondary" style={{ fontSize: 10 }}>
          No Image
        </Text>
      </View>
    );
  }

  return <Image source={{ uri: imageUrl }} style={style} />;
};

interface CatalogListScreenProps {
  onAddItem?: () => void;
  onEditItem?: (id: string) => void;
  onDeleteItem?: (id: string) => void;
  onBack?: () => void;
  onHome?: () => void;
  onManageShop?: () => void;
  onManageUser?: () => void;
  onManageOrders?: () => void;
  onWebsiteBuilder?: () => void;
  onLogout?: () => void;
}

interface Price {
  currency: string;
  value: number;
  discountPercentage?: number;
  discountedPrice?: number;
}

interface Availability {
  isAvailable: boolean;
  startTime?: string;
  endTime?: string;
}

interface Stock {
  available: number;
  reserved?: number;
  total: number;
}

interface Catalog {
  id: string;
  name: string;
  description?: string;
  category?: string;
  shopId: string;
  unit?: string;
  price?: Price;
  imageUrl?: string;
  additionalImages?: string[]; // Up to 5 additional product images
  availability?: Availability;
  stock?: number | Stock; // Support both formats for backward compatibility
}

interface CatalogForm {
  name: string;
  description: string;
  category: string;
  unit: string;
  price: string;
  currency: string;
  discountPercentage: string;
  blobPath: string | null; // Store blobPath instead of full URL
  imagePreviewUrl: string | null; // Temporary preview URL (with SAS)
  additionalImageBlobs: (string | null)[]; // Array of blob paths for additional images
  additionalImagePreviews: (string | null)[]; // Array of preview URLs for additional images
  isAvailable: boolean;
  stock: string;
}

// Catalog API Service
const catalogService = {
  getShopCatalogs: async (
    shopId: string,
    accessToken: string,
    options?: {
      limit?: number;
      cursor?: string;
      name?: string;
      description?: string;
      category?: string;
    }
  ) => {
    const config = getConfig();
    const params = new URLSearchParams();

    // Add pagination params
    if (options?.limit) {
      params.append('limit', options.limit.toString());
    }
    if (options?.cursor) {
      params.append('cursor', options.cursor);
    }

    // Add filter params
    if (options?.name) {
      params.append('name', options.name);
    }
    if (options?.description) {
      params.append('description', options.description);
    }
    if (options?.category) {
      params.append('category', options.category);
    }

    const queryString = params.toString();
    const url = `${config.apiBaseUrl}/api/catalogs/shop/${shopId}${queryString ? `?${queryString}` : ''}`;

    console.log('📡 [CatalogService] Fetching catalogs:', {
      shopId,
      url,
      filters: options,
    });

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    console.log('📡 [CatalogService] Response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      console.error('❌ [CatalogService] Error:', errorData);
      throw new Error(errorData.message || 'Failed to load catalogs');
    }

    return response.json();
  },

  createCatalog: async (data: any, accessToken: string) => {
    const config = getConfig();
    console.log('📡 [CatalogService] Creating catalog:', data);

    const response = await fetch(`${config.apiBaseUrl}/api/catalogs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    console.log('📡 [CatalogService] Create response:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      console.error('❌ [CatalogService] Create error:', errorData);
      console.error('❌ [CatalogService] Full error details:', JSON.stringify(errorData, null, 2));

      // Provide more detailed error message
      const errorMessage = errorData.message || errorData.error || 'Failed to create catalog';
      throw new Error(errorMessage);
    }

    return response.json();
  },

  updateCatalog: async (catalogId: string, data: any, accessToken: string) => {
    const config = getConfig();
    console.log('📡 [CatalogService] Updating catalog:', catalogId);

    const response = await fetch(`${config.apiBaseUrl}/api/catalogs/${catalogId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    console.log('📡 [CatalogService] Update response:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      console.error('❌ [CatalogService] Update error:', errorData);
      throw new Error(errorData.message || 'Failed to update catalog');
    }

    return response.json();
  },

  deleteCatalog: async (catalogId: string, accessToken: string) => {
    const config = getConfig();
    console.log('🗑️ [CatalogService] Deleting catalog:', catalogId);

    const response = await fetch(`${config.apiBaseUrl}/api/catalogs/${catalogId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    console.log('📡 [CatalogService] Delete response:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      console.error('❌ [CatalogService] Delete error:', errorData);
      throw new Error(errorData.message || 'Failed to delete catalog');
    }

    return response.json();
  },
};

export const CatalogListScreen: React.FC<CatalogListScreenProps> = ({
  onBack,
  onHome,
  onManageShop,
  onManageUser,
  onManageOrders,
  onWebsiteBuilder,
  onLogout,
}) => {
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddEdit, setShowAddEdit] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState<Catalog | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedAdditionalFiles, setSelectedAdditionalFiles] = useState<(File | null)[]>([null, null, null, null, null]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [uoms, setUoms] = useState<UnitOfMeasurement[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [uploadingCSV, setUploadingCSV] = useState(false);
  const [csvProgress, setCsvProgress] = useState({ current: 0, total: 0, message: '' });
  const [speedOnboarding, setSpeedOnboarding] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState({ current: 0, total: 0, message: '' });
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categoryInputMode, setCategoryInputMode] = useState<'dropdown' | 'custom'>('dropdown'); // Toggle between modes

  // Pagination states
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Text filter states
  const [nameFilter, setNameFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [appliedNameFilter, setAppliedNameFilter] = useState('');
  const [appliedDescriptionFilter, setAppliedDescriptionFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Image viewer modal states
  const [viewingImages, setViewingImages] = useState<{ main: string | null; additional: string[] } | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);

  // Ref for file input
  const fileInputRef = useRef<HTMLInputElement>(null);
  const additionalFileInputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null, null]);

  const [form, setForm] = useState<CatalogForm>({
    name: '',
    description: '',
    category: '',
    unit: 'Piece',
    price: '',
    currency: 'USD',
    discountPercentage: '',
    blobPath: null,
    imagePreviewUrl: null,
    additionalImageBlobs: [null, null, null, null, null], // Up to 5 additional images
    additionalImagePreviews: [null, null, null, null, null], // Previews for 5 additional images
    isAvailable: true,
    stock: '',
  });

  const [errors, setErrors] = useState<Partial<CatalogForm>>({});

  const shop = useCurrentShop();
  const tokens = useAuthTokens();
  const themeColors = useThemeColors();
  const accentColor = useThemeAccent();

  // Load configuration data (categories filtered by shop type)
  useEffect(() => {
    if (shop?.shopType) {
      loadCategoriesForShopType();
    }
  }, [shop?.shopType]);

  // Load catalogs on mount and when filters change
  useEffect(() => {
    if (shop?.id && tokens?.oauthToken) {
      setCursor(null); // Reset cursor when filters change
      setHasMore(true);
      loadCatalogs(false);
    }
  }, [shop?.id, tokens?.oauthToken, appliedNameFilter, appliedDescriptionFilter, selectedCategory]);

  const loadCategoriesForShopType = async () => {
    try {
      setLoadingConfig(true);

      console.log('📋 [Catalog] Loading configuration data for shop type:', shop?.shopType);

      if (!shop?.shopType) {
        console.warn('⚠️ [Catalog] No shop type found, cannot load categories');
        setCategories([]);
        return;
      }

      // Load categories, UOMs, and countries in parallel (uses 5-minute cache)
      const [categoriesData, uomsData, countriesData] = await Promise.all([
        configurationService.getCategories({ activeOnly: true, shopTypeCode: shop.shopType }),
        configurationService.getUnitOfMeasurements({ activeOnly: true }),
        configurationService.getCountries({ activeOnly: true })
      ]);

      console.log('✅ [Catalog] Configuration data loaded:', {
        shopType: shop.shopType,
        categoriesCount: categoriesData.length,
        uomsCount: uomsData.length,
        countriesCount: countriesData.length,
      });

      setCategories(categoriesData);
      setUoms(uomsData);
      setCountries(countriesData);

      // Set default values if not set and data is available
      if (!form.category && categoriesData.length > 0) {
        setForm((prev) => ({
          ...prev,
          category: categoriesData[0].categoryName,
        }));
      }

      if (!form.unit && uomsData.length > 0) {
        setForm((prev) => ({
          ...prev,
          unit: uomsData[0].displayName,
        }));
      }

      if (!form.currency && countriesData.length > 0) {
        setForm((prev) => ({
          ...prev,
          currency: countriesData[0].currency,
        }));
      }
    } catch (error) {
      console.error('❌ [Catalog] Failed to load configuration data:', error);
      Alert.alert(
        'Configuration Error',
        'Failed to load configuration data. Some features may not work properly.'
      );
      // Set empty arrays on error
      setCategories([]);
      setUoms([]);
      setCountries([]);
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadCatalogs = async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      if (!tokens?.oauthToken || !shop?.id) {
        throw new Error('Shop or token not found');
      }

      console.log('📡 [Catalog] Loading catalogs for shop:', shop.id, {
        isLoadMore,
        cursor: isLoadMore ? cursor : null,
        nameFilter: appliedNameFilter,
        descriptionFilter: appliedDescriptionFilter,
        categoryFilter: selectedCategory,
      });

      const data = await catalogService.getShopCatalogs(shop.id, tokens.oauthToken, {
        limit: 20,
        cursor: isLoadMore ? cursor || undefined : undefined,
        name: appliedNameFilter || undefined,
        description: appliedDescriptionFilter || undefined,
        category: selectedCategory || undefined,
      });

      console.log('✅ [Catalog] Catalogs loaded:', {
        count: Array.isArray(data) ? data.length : 0,
        hasMore: data.length === 20,
        firstItem: Array.isArray(data) && data.length > 0 ? { id: data[0].id, createdAt: (data[0] as any).createdAt } : null,
        lastItem: Array.isArray(data) && data.length > 0 ? { id: data[data.length - 1].id, createdAt: (data[data.length - 1] as any).createdAt } : null,
      });

      // Update catalogs - append if loading more, replace otherwise
      if (isLoadMore) {
        setCatalogs((prev) => [...prev, ...(Array.isArray(data) ? data : [])]);
      } else {
        setCatalogs(Array.isArray(data) ? data : []);
      }

      // Update pagination state
      if (Array.isArray(data) && data.length > 0) {
        // Extract cursor from last item's createdAt
        const lastItem = data[data.length - 1] as any;
        const nextCursor = lastItem.createdAt || lastItem.updatedAt;

        console.log('📌 [Catalog] Setting cursor for next page:', {
          nextCursor,
          hasMore: data.length === 20,
        });

        setCursor(nextCursor);
        setHasMore(data.length === 20); // If we got full page, there might be more
      } else {
        console.log('📌 [Catalog] No more items to load');
        setHasMore(false);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load catalogs';
      console.error('❌ [Catalog] Load error:', errorMessage, error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleApplyFilters = () => {
    console.log('🔍 [Catalog] Applying filters:', {
      name: nameFilter,
      description: descriptionFilter,
      category: selectedCategory,
    });

    setAppliedNameFilter(nameFilter);
    setAppliedDescriptionFilter(descriptionFilter);
    setCursor(null); // Reset cursor when applying new filters
    setHasMore(true);
    // loadCatalogs will be triggered by useEffect
  };

  const handleResetFilters = () => {
    console.log('🔄 [Catalog] Resetting all filters');

    setNameFilter('');
    setDescriptionFilter('');
    setAppliedNameFilter('');
    setAppliedDescriptionFilter('');
    setSelectedCategory('');
    setCursor(null);
    setHasMore(true);
    // loadCatalogs will be triggered by useEffect
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore && cursor) {
      console.log('📄 [Catalog] Loading more catalogs with cursor:', cursor);
      loadCatalogs(true);
    }
  };

  const handleAddNew = () => {
    setEditingCatalog(null);
    setSelectedFile(null);
    setSelectedAdditionalFiles([null, null, null, null, null]);
    setForm({
      name: '',
      description: '',
      category: '',
      unit: 'Piece',
      price: '1.0', // Default to 1.0 to ensure price > 0
      currency: 'INR',
      discountPercentage: '',
      blobPath: null,
      imagePreviewUrl: null,
      additionalImageBlobs: [null, null, null, null, null],
      additionalImagePreviews: [null, null, null, null, null],
      isAvailable: true,
      stock: '0',
    });
    setErrors({});
    setShowAddEdit(true);
  };

  const handleEdit = async (catalog: Catalog) => {
    setEditingCatalog(catalog);
    setSelectedFile(null);
    setSelectedAdditionalFiles([null, null, null, null, null]);

    // Handle stock - could be number (old format) or object (new format)
    let stockValue = '';
    if (catalog.stock) {
      if (typeof catalog.stock === 'number') {
        stockValue = catalog.stock.toString();
      } else if (typeof catalog.stock === 'object' && catalog.stock.available !== undefined) {
        stockValue = catalog.stock.available.toString();
      } else if (typeof catalog.stock === 'object' && catalog.stock.total !== undefined) {
        stockValue = catalog.stock.total.toString();
      }
    }

    // Load additional image blobs
    const additionalBlobs: (string | null)[] = [null, null, null, null, null];
    if (catalog.additionalImages && Array.isArray(catalog.additionalImages)) {
      catalog.additionalImages.forEach((img, idx) => {
        if (idx < 5 && img) {
          additionalBlobs[idx] = img;
        }
      });
    }

    setForm({
      name: catalog.name,
      description: catalog.description || '',
      category: catalog.category || '',
      unit: catalog.unit || 'Piece',
      price: catalog.price?.value?.toString() || '',
      currency: catalog.price?.currency || 'INR',
      discountPercentage: catalog.price?.discountPercentage?.toString() || '',
      blobPath: catalog.imageUrl || null, // Store blobPath from imageUrl field
      imagePreviewUrl: null, // Will be loaded below
      additionalImageBlobs: additionalBlobs,
      additionalImagePreviews: [null, null, null, null, null], // Will be loaded below
      isAvailable: catalog.availability?.isAvailable ?? true,
      stock: stockValue,
    });

    // Load preview URL if image exists
    if (catalog.imageUrl) {
      await loadImagePreview(catalog.imageUrl);
    }

    // Load additional image previews
    for (let i = 0; i < additionalBlobs.length; i++) {
      if (additionalBlobs[i]) {
        await loadAdditionalImagePreview(i, additionalBlobs[i]!);
      }
    }

    setErrors({});
    setShowAddEdit(true);
  };

  // Load image preview URL from blobPath
  const loadImagePreview = async (blobPath: string) => {
    try {
      console.log('📥 [Catalog] Loading preview URL for blobPath:', blobPath);
      const readUrl = await storageService.getReadUrl(blobPath);
      console.log('✅ [Catalog] Preview URL loaded');

      setForm((prev) => ({
        ...prev,
        imagePreviewUrl: readUrl,
      }));
    } catch (error) {
      console.error('❌ [Catalog] Failed to load preview URL:', error);
    }
  };

  // Load additional image preview URL from blobPath
  const loadAdditionalImagePreview = async (index: number, blobPath: string) => {
    try {
      console.log(`📥 [Catalog] Loading additional image ${index} preview URL for blobPath:`, blobPath);
      const readUrl = await storageService.getReadUrl(blobPath);
      console.log(`✅ [Catalog] Additional image ${index} preview URL loaded`);

      setForm((prev) => {
        const newPreviews = [...prev.additionalImagePreviews];
        newPreviews[index] = readUrl;
        return {
          ...prev,
          additionalImagePreviews: newPreviews,
        };
      });
    } catch (error) {
      console.error(`❌ [Catalog] Failed to load additional image ${index} preview URL:`, error);
    }
  };

  // Handle file selection (validation and preview)
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    console.log('📁 [Catalog] File selected:', {
      name: file.name,
      type: file.type,
      size: file.size,
    });

    // Validate file
    const validation = storageService.validateFile(file);
    if (!validation.valid) {
      Alert.alert('Invalid File', validation.error || 'Please select a valid image file');
      return;
    }

    // Set selected file and create local preview
    setSelectedFile(file);
    const localPreviewUrl = URL.createObjectURL(file);
    setForm((prev) => ({
      ...prev,
      imagePreviewUrl: localPreviewUrl,
    }));

    console.log('✅ [Catalog] File validated and preview set');
  };

  // Handle file upload to Azure (returns blobPath)
  const handleImageUpload = async (): Promise<string | null> => {
    if (!selectedFile || !shop?.id) {
      return null;
    }

    console.log('☁️ [Catalog] Starting secure upload...');
    setUploadingImage(true);
    setUploadProgress(0);

    try {
      const blobPath = await storageService.uploadImage(
        selectedFile,
        shop.id,
        'catalog',
        editingCatalog?.id, // Use existing item ID if editing
        (progress: UploadProgress) => {
          setUploadProgress(progress.percentage);
          console.log(`📊 [Catalog] Upload progress: ${progress.percentage}%`);
        }
      );

      console.log('✅ [Catalog] Upload completed, blobPath:', blobPath);

      // Load preview URL for the uploaded blob
      await loadImagePreview(blobPath);

      setUploadingImage(false);
      setUploadProgress(100);

      return blobPath;
    } catch (error) {
      console.error('❌ [Catalog] Upload failed:', error);
      setUploadingImage(false);
      setUploadProgress(0);

      const errorMessage = error instanceof Error ? error.message : 'Failed to upload image';
      Alert.alert('Upload Failed', errorMessage);

      return null;
    }
  };

  // Handle additional image upload to Azure (returns blobPath)
  const handleAdditionalImageUpload = async (index: number, file: File): Promise<string | null> => {
    if (!file || !shop?.id || index < 0 || index >= 5) {
      return null;
    }

    console.log(`☁️ [Catalog] Starting additional image ${index + 1} upload...`);

    try {
      const blobPath = await storageService.uploadImage(
        file,
        shop.id,
        'catalog',
        editingCatalog?.id, // Use existing item ID if editing
        (progress: UploadProgress) => {
          console.log(`📊 [Catalog] Additional image ${index + 1} progress: ${progress.percentage}%`);
        }
      );

      console.log(`✅ [Catalog] Additional image ${index + 1} uploaded, blobPath:`, blobPath);

      // Load preview URL for the uploaded blob
      const previewUrl = await storageService.getReadUrl(blobPath);

      // Update form with blob path and preview
      setForm((prev) => {
        const newAdditionalBlobs = [...prev.additionalImageBlobs];
        const newAdditionalPreviews = [...prev.additionalImagePreviews];
        newAdditionalBlobs[index] = blobPath;
        newAdditionalPreviews[index] = previewUrl;

        return {
          ...prev,
          additionalImageBlobs: newAdditionalBlobs,
          additionalImagePreviews: newAdditionalPreviews,
        };
      });

      return blobPath;
    } catch (error) {
      console.error(`❌ [Catalog] Additional image ${index + 1} upload failed:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload image';
      Alert.alert('Upload Failed', errorMessage);
      return null;
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setForm((prev) => ({
      ...prev,
      blobPath: null,
      imagePreviewUrl: null,
    }));
    console.log('✅ [Catalog] Image removed from form');
  };

  const handleRemoveAdditionalImage = (index: number) => {
    if (index < 0 || index >= 5) return;

    setForm((prev) => {
      const newAdditionalBlobs = [...prev.additionalImageBlobs];
      const newAdditionalPreviews = [...prev.additionalImagePreviews];
      const newSelectedFiles = [...selectedAdditionalFiles];

      newAdditionalBlobs[index] = null;
      newAdditionalPreviews[index] = null;
      newSelectedFiles[index] = null;

      setSelectedAdditionalFiles(newSelectedFiles);

      return {
        ...prev,
        additionalImageBlobs: newAdditionalBlobs,
        additionalImagePreviews: newAdditionalPreviews,
      };
    });

    console.log(`✅ [Catalog] Additional image ${index + 1} removed from form`);
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAdditionalImageBrowseClick = (index: number) => {
    if (additionalFileInputRefs.current[index]) {
      additionalFileInputRefs.current[index]?.click();
    }
  };

  const handleImageClick = (mainImage: string | null, additionalImages: string[] = []) => {
    console.log('🖼️ [Catalog] Opening image modal', { mainImage, additionalCount: additionalImages.length });
    setViewingImages({ main: mainImage, additional: additionalImages });
    setCurrentImageIndex(0);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setViewingImages(null);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    if (!viewingImages) return;
    const totalImages = 1 + viewingImages.additional.length;
    setCurrentImageIndex((prev) => (prev + 1) % totalImages);
  };

  const handlePrevImage = () => {
    if (!viewingImages) return;
    const totalImages = 1 + viewingImages.additional.length;
    setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
  };

  const getCurrentImage = (): string | null => {
    if (!viewingImages) return null;
    const allImages = viewingImages.main ? [viewingImages.main, ...viewingImages.additional] : viewingImages.additional;
    return allImages[currentImageIndex] || null;
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CatalogForm> = {};

    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!form.category.trim()) {
      newErrors.category = 'Category is required';
    }

    if (!form.unit.trim()) {
      newErrors.unit = 'Unit is required';
    }

    if (!form.currency.trim()) {
      newErrors.currency = 'Currency is required';
    }

    if (!form.price.trim()) {
      newErrors.price = 'Price is required';
    } else if (isNaN(parseFloat(form.price)) || parseFloat(form.price) <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  /**
   * Common function to build catalog API payload
   */
  const buildCatalogPayload = (data: {
    name: string;
    description?: string;
    category?: string;
    unit?: string;
    price: number;
    currency?: string;
    discountPercentage?: number;
    stock?: number;
    imageUrl?: string; // blobPath
    additionalImages?: string[]; // array of blobPaths
    isAvailable?: boolean;
    shopId: string;
  }) => {
    const stockValue = data.stock && data.stock > 0 ? data.stock : 0;

    const catalogData: any = {
      shopId: data.shopId,
      name: data.name.trim(),
      description: data.description?.trim() || '',
      category: data.category?.trim() || '',
      unit: data.unit || 'Piece',
      price: {
        currency: data.currency || 'INR',
        value: data.price,
      },
      availability: {
        isAvailable: data.isAvailable !== undefined ? data.isAvailable : true,
      },
      stock: {
        available: stockValue,
        reserved: 0,
        total: stockValue
      }
    };

    // Add discount only if provided and valid
    if (data.discountPercentage && data.discountPercentage > 0) {
      catalogData.price.discountPercentage = data.discountPercentage;
    }

    // Add image URL if provided
    if (data.imageUrl && data.imageUrl.trim()) {
      catalogData.imageUrl = data.imageUrl.trim();
    }

    // Add additional images if provided
    if (data.additionalImages && data.additionalImages.length > 0) {
      catalogData.additionalImages = data.additionalImages.map((img) => img.trim()).filter((img) => img);
    }


    return catalogData;
  };

  const handleSave = async () => {
    if (!validateForm() || !tokens?.oauthToken || !shop?.id) {
      return;
    }

    setSaving(true);

    try {
      // Upload main image if a new file was selected
      let finalBlobPath = form.blobPath;
      if (selectedFile) {
        console.log('📤 [Catalog] Uploading new main image...');
        const uploadedBlobPath = await handleImageUpload();
        if (uploadedBlobPath) {
          finalBlobPath = uploadedBlobPath;
          console.log('✅ [Catalog] Main image uploaded, blobPath:', uploadedBlobPath);
        }
      }

      // Upload additional images if any new files were selected
      const finalAdditionalBlobs: string[] = [];
      for (let i = 0; i < 5; i++) {
        if (selectedAdditionalFiles[i]) {
          console.log(`📤 [Catalog] Uploading additional image ${i + 1}...`);
          const uploadedBlobPath = await handleAdditionalImageUpload(i, selectedAdditionalFiles[i]!);
          if (uploadedBlobPath) {
            finalAdditionalBlobs.push(uploadedBlobPath);
            console.log(`✅ [Catalog] Additional image ${i + 1} uploaded, blobPath:`, uploadedBlobPath);
          }
        } else if (form.additionalImageBlobs[i]) {
          // Keep existing blob path if no new file was selected
          finalAdditionalBlobs.push(form.additionalImageBlobs[i]!);
        }
      }

      // Build catalog data using common function
      const priceValue = parseFloat(form.price);
      if (isNaN(priceValue) || priceValue <= 0) {
        throw new Error('Price must be greater than 0');
      }

      const catalogData = buildCatalogPayload({
        name: form.name,
        description: form.description,
        category: form.category,
        unit: form.unit,
        price: priceValue,
        currency: form.currency,
        discountPercentage: form.discountPercentage ? parseFloat(form.discountPercentage) : undefined,
        stock: form.stock ? parseInt(form.stock) : 0,
        imageUrl: finalBlobPath || undefined, // Send blobPath as imageUrl
        additionalImages: finalAdditionalBlobs.length > 0 ? finalAdditionalBlobs : undefined, // Send additional image blob paths
        isAvailable: form.isAvailable,
        shopId: shop.id
      });

      console.log('📦 [Catalog] Saving catalog with blobPath:', finalBlobPath);
      console.log('📦 [Catalog] Additional images count:', finalAdditionalBlobs.length);
      console.log('📦 [Catalog] Full catalog data:', JSON.stringify(catalogData, null, 2));

      if (editingCatalog) {
        // Update existing catalog
        console.log('📝 [Catalog] Updating catalog:', editingCatalog.id);
        const updated = await catalogService.updateCatalog(editingCatalog.id, catalogData, tokens.oauthToken);
        setCatalogs((prev) => prev.map((c) => (c.id === editingCatalog.id ? updated : c)));
        console.log('✅ [Catalog] Updated successfully');
      } else {
        // Create new catalog
        console.log('➕ [Catalog] Creating new catalog');
        const created = await catalogService.createCatalog(catalogData, tokens.oauthToken);
        console.log('✅ [Catalog] Created:', created);
        setCatalogs((prev) => [...prev, created]);
        console.log('✅ [Catalog] Created successfully');
      }

      // Clean up
      setSelectedFile(null);
      setSelectedAdditionalFiles([null, null, null, null, null]);
      setShowAddEdit(false);
      setEditingCatalog(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save catalog';
      console.error('❌ [Catalog] Save error:', errorMessage, error);
      alert(`❌ Error: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Unified Share Function - Uses Device Native Share with Image Attachment
   */
  const handleShare = async (catalog: Catalog) => {
    try {
      const shopMenuUrl = getConfig().customerShopMenuBaseUrl;
      const shopId = shop?.id;
      const fullUrl = `${shopMenuUrl}?shopid=${shopId}`;

      // Build share message with shop URL first, then image reference
      let message = `Find Our Exciting Product: ${catalog.name}\n\n`;

      // Add shop menu URL first
      message += `🏪 View in Shop: ${fullUrl}`;

      console.log('🔗 [Share] Opening native share dialog:', {
        catalog: catalog.name,
        shopId,
        shopUrl: fullUrl,
        imageUrl: catalog.imageUrl,
        fullMessage: message
      });

      // Try to share with image as attachment
      if (navigator.share && catalog.imageUrl) {
        try {
          console.log('📥 [Share] Downloading image for attachment:', catalog.imageUrl);

          // Download image
          const response = await fetch(catalog.imageUrl);
          const blob = await response.blob();

          // Create a File object from the blob
          const fileName = `${catalog.name.replace(/\s+/g, '-')}.png`;
          const file = new File([blob], fileName, { type: 'image/png' });

          console.log('✅ [Share] Image downloaded successfully:', {
            fileName,
            fileSize: blob.size,
            fileType: blob.type
          });

          // Share with image file
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `Check out ${catalog.name}!`,
              text: message,
              files: [file]
            }).catch((error) => {
              if (error.name !== 'AbortError') {
                console.error('Share with file error:', error);
                // Fallback to share without file
                shareWithoutFile(message, fullUrl);
              }
            });
          } else {
            // Share without file if not supported
            console.log('📋 [Share] File sharing not supported, sharing text only');
            shareWithoutFile(message, fullUrl);
          }
        } catch (imageError) {
          console.error('❌ [Share] Image download error:', imageError);
          // Fallback to sharing without image
          shareWithoutFile(message, fullUrl);
        }
      } else if (navigator.share) {
        // Share without image if URL not available
        shareWithoutFile(message, fullUrl);
      } else {
        // Fallback: Copy to clipboard for non-Web Share API browsers
        fallbackShareToClipboard(message);
      }
    } catch (error) {
      console.error('❌ [Share] Error:', error);
      Alert.alert('Share Error', 'Could not open share dialog. Please try again.');
    }
  };

  /**
   * Share without image file (fallback)
   */
  const shareWithoutFile = async (message: string, fullUrl: string) => {
    try {
      await navigator.share({
        title: 'Check out this product!',
        text: message,
        url: fullUrl
      }).catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Share error:', error);
          fallbackShareToClipboard(message);
        }
      });
    } catch (error) {
      console.error('❌ [Share] Fallback share error:', error);
      fallbackShareToClipboard(message);
    }
  };

  /**
   * Fallback: Copy to clipboard (for browsers without Web Share API)
   */
  const fallbackShareToClipboard = (message: string) => {
    navigator.clipboard.writeText(message).then(() => {
      Alert.alert(
        '📋 Share Product',
        `Product information copied to clipboard:\n\n${message}\n\nYou can now paste and share on your favorite platform.`
      );
      console.log('✅ [Share] Message copied to clipboard');
    }).catch((error) => {
      console.error('❌ [Share] Copy error:', error);
      Alert.alert(
        '📋 Share Product',
        `Share this product:\n\n${message}`
      );
    });
  };

  const handleDelete = async (catalog: Catalog) => {
    try {
      if (!tokens?.oauthToken) {
        throw new Error('Token not found');
      }

      console.log('🗑️ [Catalog] Deleting catalog:', catalog.id);
      await catalogService.deleteCatalog(catalog.id, tokens.oauthToken);
      setCatalogs((prev) => prev.filter((c) => c.id !== catalog.id));
      console.log('✅ [Catalog] Deleted successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete catalog';
      console.error('❌ [Catalog] Delete error:', errorMessage, error);
      alert(`❌ Error: ${errorMessage}`);
    }
  };

  /**
   * Download catalogs as CSV
   */
  const handleDownloadCSV = () => {
    try {
      console.log('📥 [CSV] Downloading catalogs as CSV...');

      if (catalogs.length === 0) {
        console.log('ℹ️ [CSV] No catalogs to download');
        return;
      }

      // CSV Headers
      const headers = [
        'Catalog ID',
        'Shop ID',
        'Name',
        'Description',
        'Category',
        'Unit',
        'Price',
        'Currency',
        'Discount %',
        'Discounted Price',
        'Stock',
        'Image URL',
        'Is Available'
      ];

      // Convert catalogs to CSV rows
      const rows = catalogs.map(catalog => {
        const stock = typeof catalog.stock === 'object' ? catalog.stock.total : (catalog.stock || 0);
        return [
          catalog.id || '',
          catalog.shopId || shop?.id || '',
          catalog.name || '',
          catalog.description || '',
          catalog.category || '',
          catalog.unit || '',
          catalog.price?.value || '',
          catalog.price?.currency || 'USD',
          catalog.price?.discountPercentage || '',
          catalog.price?.discountedPrice || '',
          stock,
          catalog.imageUrl || '',
          catalog.availability?.isAvailable !== false ? 'TRUE' : 'FALSE'
        ];
      });

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `${shop?.name || 'shop'}_catalogs_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('✅ [CSV] Downloaded successfully');
      console.log(`✅ [CSV] Downloaded ${catalogs.length} catalog items as CSV`);
    } catch (error) {
      console.error('❌ [CSV] Download error:', error);
      console.error('❌ [CSV] Failed to download CSV. Please try again.');
    }
  };

  /**
   * Download CSV template with sample data
   */
  const handleDownloadTemplate = () => {
    try {
      console.log('📄 [CSV Template] Downloading CSV template...');

      // CSV Headers
      const headers = [
        'Catalog ID',
        'Shop ID',
        'Name',
        'Description',
        'Category',
        'Unit',
        'Price',
        'Currency',
        'Discount %',
        'Discounted Price',
        'Stock',
        'Image URL',
        'Is Available'
      ];

      // Sample rows with instructions
      const sampleRows = [
        [
          '',
          shop?.id || 'your-shop-id',
          'Sample Product 1',
          'Product description here',
          'Category Name',
          'Piece',
          '10.99',
          'USD',
          '10',
          '9.89',
          '100',
          'https://example.com/image.jpg',
          'TRUE'
        ],
        [
          '',
          shop?.id || 'your-shop-id',
          'Sample Product 2',
          'Another product description',
          'Category Name',
          'Kg',
          '5.50',
          'USD',
          '',
          '',
          '50',
          '',
          'TRUE'
        ],
        [
          '(Leave empty for new items)',
          '(Your shop ID)',
          '(Required)',
          '(Optional)',
          '(Optional)',
          '(Required: Piece, Kg, etc.)',
          '(Required)',
          '(Required: USD, EUR, etc.)',
          '(Optional)',
          '(Auto-calculated)',
          '(Optional)',
          '(Optional)',
          '(TRUE or FALSE)'
        ]
      ];

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...sampleRows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `catalog_template_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('✅ [CSV Template] Downloaded successfully');
      alert('✅ CSV Template downloaded!\n\nThe template includes:\n• Sample data rows\n• Instructions in the last row\n• All required columns\n\nFill in your catalog data and upload!');
    } catch (error) {
      console.error('❌ [CSV Template] Download error:', error);
      alert('Failed to download template. Please try again.');
    }
  };

  /**
   * Upload and process CSV file
   */
  const handleUploadCSV = async () => {
    try {
      // Create file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.csv';

      input.onchange = async (e: any) => {
        const file = e.target.files[0];
        if (!file) return;

        console.log('📤 [CSV] Selected file:', file.name);

        try {
          setUploadingCSV(true);
          setCsvProgress({ current: 0, total: 0, message: 'Reading CSV file...' });

          // Read file
          const text = await file.text();
          const lines = text.split('\n').filter(line => line.trim());

          if (lines.length < 2) {
            alert('CSV file is empty or invalid');
            setUploadingCSV(false);
            return;
          }

          // Parse CSV
          const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
          const dataRows = lines.slice(1);

          console.log('📊 [CSV] Found', dataRows.length, 'rows');
          setCsvProgress({ current: 0, total: dataRows.length, message: 'Processing catalogs...' });

          let successCount = 0;
          let errorCount = 0;
          const errors: string[] = [];

          // Process each row
          for (let i = 0; i < dataRows.length; i++) {
            const row = dataRows[i];

            // Parse CSV properly - split by comma and handle empty values
            const values = row.split(',').map(v => v.trim());

            if (values.length < 7) { // Need at least: ID, ShopID, Name, Description, Category, Unit, Price
              console.warn('⚠️ [CSV Upload] Skipping invalid row', i + 1, '- only', values.length, 'columns');
              continue;
            }

            try {
              // CSV columns: Catalog ID,Shop ID,Name,Description,Category,Unit,Price,Currency,Discount %,Discounted Price,Stock,Image URL,Is Available
              // Index:        0         1       2    3           4        5    6     7        8           9                10    11        12

              const catalogId = values[0]?.trim();                     // Catalog ID (index 0)
              const shopIdFromCSV = values[1]?.trim();                 // Shop ID (index 1)
              const name = values[2]?.trim();                          // Name (index 2)
              const description = values[3]?.trim() || '';             // Description (index 3)
              const category = values[4]?.trim() || 'Other';           // Category (index 4)
              const unit = values[5]?.trim() || 'Piece';               // Unit (index 5)
              const priceValue = parseFloat(values[6]) || 1.0;         // Price (index 6)
              const price = isNaN(priceValue) || priceValue <= 0 ? 1.0 : priceValue; // Default to 1.0 if invalid
              const currency = values[7]?.trim() || 'INR';             // Currency (index 7)
              const discountPercentage = values[8] ? parseFloat(values[8]) : undefined; // Discount % (index 8)
              const discountedPrice = values[9] ? parseFloat(values[9]) : undefined; // Discounted Price (index 9) - not used
              const stockValue = values[10] ? parseInt(values[10]) : 0; // Stock (index 10)
              const imageUrl = values[11]?.trim() || undefined;        // Image URL (index 11)
              const isAvailableValue = values[12]?.trim().toUpperCase(); // Is Available (index 12)
              const isAvailable = isAvailableValue === 'TRUE' || isAvailableValue === '1' || isAvailableValue === 'YES';

              if (!name) {
                console.warn('⚠️ [CSV Upload] Skipping row with empty name', i + 1);
                continue;
              }

              console.log('🔍 [CSV Upload] Row parsed:', {
                row: i + 1,
                catalogId,
                name,
                description,
                category,
                unit,
                price,
                currency,
                stockValue,
                isAvailable,
                rawValues: values
              });

              // Build catalog data using common function
              const catalogData = buildCatalogPayload({
                name,
                description,
                category,
                unit,
                price,
                currency,
                discountPercentage,
                stock: stockValue,
                imageUrl,
                isAvailable,
                shopId: shopIdFromCSV || shop?.id || ''
              });

              // Determine if update or create
              const isUpdate = catalogId && catalogId.trim() && shopIdFromCSV === shop?.id;

              setCsvProgress({
                current: i + 1,
                total: dataRows.length,
                message: `${isUpdate ? 'Updating' : 'Creating'}: ${name}...`
              });

              if (isUpdate) {
                // Update existing catalog
                console.log('🔄 [CSV] Updating catalog:', catalogId);
                await catalogService.updateCatalog(catalogId, catalogData, tokens?.oauthToken || '');
              } else {
                // Create new catalog
                console.log('➕ [CSV] Creating catalog:', name);
                await catalogService.createCatalog(catalogData, tokens?.oauthToken || '');
              }

              successCount++;
            } catch (error) {
              errorCount++;
              const errorMsg = error instanceof Error ? error.message : 'Unknown error';
              errors.push(`Row ${i + 2}: ${errorMsg}`);
              console.error(`❌ [CSV] Error processing row ${i + 2}:`, error);
            }
          }

          // Reload catalogs
          setCsvProgress({ current: dataRows.length, total: dataRows.length, message: 'Refreshing catalog list...' });
          await loadCatalogs();

          // Show results
          setUploadingCSV(false);
          setCsvProgress({ current: 0, total: 0, message: '' });

          let message = `✅ CSV Upload Complete!\n\n`;
          message += `Success: ${successCount} items\n`;
          if (errorCount > 0) {
            message += `Failed: ${errorCount} items\n\n`;
            if (errors.length > 0) {
              message += `Errors:\n${errors.slice(0, 5).join('\n')}`;
              if (errors.length > 5) {
                message += `\n... and ${errors.length - 5} more`;
              }
            }
          }
          alert(message);

        } catch (error) {
          console.error('❌ [CSV] Upload error:', error);
          alert('Failed to process CSV file. Please check the format and try again.');
          setUploadingCSV(false);
          setCsvProgress({ current: 0, total: 0, message: '' });
        }
      };

      input.click();
    } catch (error) {
      console.error('❌ [CSV] Upload error:', error);
      alert('Failed to upload CSV. Please try again.');
    }
  };

  /**
   * Speed Onboarding: Create Top 10 Vegetable Shop Items
   */
  const handleSpeedOnboarding = async () => {
    try {
      if (!shop?.id || !tokens?.oauthToken) {
        alert('Shop information not available. Please try again.');
        return;
      }

      console.log('🚀 [Speed Onboarding] Starting...');
      setSpeedOnboarding(true);

      // Top 10 most common vegetables with realistic Indian market data
      const commonVegetables = [
        { name: 'Tomato', description: 'Fresh red tomatoes', category: 'Vegetables', unit: 'Kg', price: '40', currency: 'INR' },
        { name: 'Onion', description: 'Premium quality onions', category: 'Vegetables', unit: 'Kg', price: '30', currency: 'INR' },
        { name: 'Potato', description: 'Fresh potatoes', category: 'Vegetables', unit: 'Kg', price: '25', currency: 'INR' },
        { name: 'Cabbage', description: 'Fresh green cabbage', category: 'Leafy Vegetables', unit: 'Kg', price: '20', currency: 'INR' },
        { name: 'Cauliflower', description: 'Premium cauliflower', category: 'Vegetables', unit: 'Kg', price: '35', currency: 'INR' },
        { name: 'Carrot', description: 'Fresh orange carrots', category: 'Root Vegetables', unit: 'Kg', price: '45', currency: 'INR' },
        { name: 'Beans', description: 'Fresh green beans', category: 'Pods & Beans', unit: 'Kg', price: '50', currency: 'INR' },
        { name: 'Capsicum', description: 'Fresh bell peppers', category: 'Vegetables', unit: 'Kg', price: '60', currency: 'INR' },
        { name: 'Brinjal', description: 'Fresh eggplant/brinjal', category: 'Vegetables', unit: 'Kg', price: '30', currency: 'INR' },
        { name: 'Cucumber', description: 'Fresh cucumbers', category: 'Vegetables', unit: 'Kg', price: '25', currency: 'INR' }
      ];

      setOnboardingProgress({ current: 0, total: commonVegetables.length, message: 'Creating catalog items...' });

      let successCount = 0;
      let errorCount = 0;

      // Process each vegetable
      for (let i = 0; i < commonVegetables.length; i++) {
        const veg = commonVegetables[i];

        try {
          const catalogData = buildCatalogPayload({
            name: veg.name,
            description: veg.description,
            category: veg.category,
            unit: veg.unit,
            price: veg.price,
            currency: veg.currency,
            discountPercentage: '',
            stock: '100',
            imageUrl: null,
            isAvailable: true,
            shopId: shop.id
          });

          setOnboardingProgress({
            current: i + 1,
            total: commonVegetables.length,
            message: `Adding: ${veg.name}...`
          });

          console.log('➕ [Speed Onboarding] Creating:', veg.name);
          const result = await catalogService.createCatalog(catalogData, tokens.oauthToken);
          console.log('✅ [Speed Onboarding] Created successfully:', result);
          successCount++;

          // Small delay to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 100));

        } catch (error) {
          errorCount++;
          console.error(`❌ [Speed Onboarding] Error creating ${veg.name}:`, error);
        }
      }

      // Reload catalogs
      setOnboardingProgress({ current: commonVegetables.length, total: commonVegetables.length, message: 'Refreshing catalog...' });
      await loadCatalogs();

      // Show success message
      setSpeedOnboarding(false);
      setOnboardingProgress({ current: 0, total: 0, message: '' });

      if (successCount > 0) {
        alert(`🎉 Congratulations!\n\nYour Initial Shop Items are added!\n\n✅ Successfully added: ${successCount} items\n\nYou can now add, edit, or delete these items as per your shop menu.\n\nRemember to:\n• Add product images\n• Update descriptions\n• Adjust prices for your location`);
      } else {
        alert(`⚠️ Speed Onboarding completed with issues.\n\nFailed: ${errorCount} items\n\nPlease try again or add items manually.`);
      }

    } catch (error) {
      console.error('❌ [Speed Onboarding] Error:', error);
      alert('Failed to create quick catalog. Please try again or add items manually.');
      setSpeedOnboarding(false);
      setOnboardingProgress({ current: 0, total: 0, message: '' });
    }
  };

  // Show info for quick start feature
  const showQuickStartInfo = () => {
    const message = shop?.shopType === 'VEGETABLE_SHOP'
      ? 'Get started quickly with our pre-defined catalog of 10 popular vegetables. These are initial items for speed onboarding - you can update images, prices, and descriptions later.'
      : 'Get started quickly with our pre-defined catalog of 10 popular restaurant items. These are initial items for speed onboarding - you can update images, prices, and descriptions later.';

    // Use browser alert for web compatibility
    if (typeof window !== 'undefined') {
      window.alert(`Quick Catalog Creation\n\n${message}`);
    } else {
      Alert.alert(
        'Quick Catalog Creation',
        message,
        [{ text: 'Got it!', style: 'default' }]
      );
    }
  };

  /**
   * Speed Onboarding: Create Top 10 Restaurant Menu Items
   */
  const handleSpeedOnboardingRestaurant = async () => {
    try {
      if (!shop?.id || !tokens?.oauthToken) {
        alert('Shop information not available. Please try again.');
        return;
      }

      console.log('🚀 [Speed Onboarding Restaurant] Starting...');
      setSpeedOnboarding(true);

      // Top 10 most common restaurant items with realistic Indian restaurant data
      const commonMenuItems = [
        { name: 'Butter Chicken', description: 'Creamy tomato-based chicken curry', category: 'Main Course - Non-Veg', unit: 'Portion', price: '250', currency: 'INR' },
        { name: 'Paneer Tikka Masala', description: 'Cottage cheese in rich gravy', category: 'Main Course - Veg', unit: 'Portion', price: '220', currency: 'INR' },
        { name: 'Dal Makhani', description: 'Black lentils in creamy sauce', category: 'Main Course - Veg', unit: 'Portion', price: '180', currency: 'INR' },
        { name: 'Garlic Naan', description: 'Tandoor-baked bread with garlic', category: 'Breads', unit: 'Piece', price: '50', currency: 'INR' },
        { name: 'Tandoori Roti', description: 'Whole wheat flatbread', category: 'Breads', unit: 'Piece', price: '30', currency: 'INR' },
        { name: 'Biryani', description: 'Aromatic rice with spices', category: 'Rice & Biryani', unit: 'Portion', price: '200', currency: 'INR' },
        { name: 'Raita', description: 'Yogurt with cucumber and spices', category: 'Sides', unit: 'Bowl', price: '60', currency: 'INR' },
        { name: 'Green Salad', description: 'Fresh mixed vegetables', category: 'Salads', unit: 'Plate', price: '80', currency: 'INR' },
        { name: 'Gulab Jamun', description: 'Sweet milk dumplings in syrup', category: 'Desserts', unit: 'Piece', price: '40', currency: 'INR' },
        { name: 'Masala Chai', description: 'Spiced Indian tea', category: 'Beverages', unit: 'Cup', price: '20', currency: 'INR' }
      ];

      setOnboardingProgress({ current: 0, total: commonMenuItems.length, message: 'Creating catalog items...' });

      let successCount = 0;
      let errorCount = 0;

      // Process each menu item
      for (let i = 0; i < commonMenuItems.length; i++) {
        const item = commonMenuItems[i];

        try {
          const catalogData = buildCatalogPayload({
            name: item.name,
            description: item.description,
            category: item.category,
            unit: item.unit,
            price: item.price,
            currency: item.currency,
            discountPercentage: '',
            stock: '50',
            imageUrl: null,
            isAvailable: true,
            shopId: shop.id
          });

          setOnboardingProgress({
            current: i + 1,
            total: commonMenuItems.length,
            message: `Adding: ${item.name}...`
          });

          console.log('➕ [Speed Onboarding Restaurant] Creating:', item.name);
          const result = await catalogService.createCatalog(catalogData, tokens.oauthToken);
          console.log('✅ [Speed Onboarding Restaurant] Created successfully:', result);
          successCount++;

          // Small delay to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 100));

        } catch (error) {
          errorCount++;
          console.error(`❌ [Speed Onboarding Restaurant] Error creating ${item.name}:`, error);
        }
      }

      // Reload catalogs
      setOnboardingProgress({ current: commonMenuItems.length, total: commonMenuItems.length, message: 'Refreshing catalog...' });
      await loadCatalogs();

      // Show success message
      setSpeedOnboarding(false);
      setOnboardingProgress({ current: 0, total: 0, message: '' });

      if (successCount > 0) {
        alert(`🎉 Congratulations!\n\nYour Initial Restaurant Menu Items are added!\n\n✅ Successfully added: ${successCount} items\n\nYou can now add, edit, or delete these items as per your restaurant menu.\n\nRemember to:\n• Add product images\n• Update descriptions\n• Adjust prices for your location`);
      } else {
        alert(`⚠️ Speed Onboarding completed with issues.\n\nFailed: ${errorCount} items\n\nPlease try again or add items manually.`);
      }

    } catch (error) {
      console.error('❌ [Speed Onboarding Restaurant] Error:', error);
      alert('Failed to create quick catalog. Please try again or add items manually.');
      setSpeedOnboarding(false);
      setOnboardingProgress({ current: 0, total: 0, message: '' });
    }
  };


  if (loading || loadingConfig) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: themeColors.secondary }]}>
        <ActivityIndicator size="large" color={accentColor} />
        <Text variant="body" color="secondary" style={styles.loadingText}>
          {loadingConfig ? 'Loading configuration...' : 'Loading catalogs...'}
        </Text>
        {loadingConfig && (
          <Text variant="caption" color="secondary" style={styles.loadingSubtext}>
            First load may take a moment
          </Text>
        )}
      </View>
    );
  }

  return (
    <>
      {/* Shop Header with Navigation Mode */}
      <ShopHeader
        mode="navigation"
        title="Manage Catalog"
        onHome={onHome || onBack}
        onManageShop={onManageShop}
        onManageUser={onManageUser}
        onManageCatalog={onBack}
        onManageOrders={onManageOrders}
        onWebsiteBuilder={onWebsiteBuilder}
        onLogout={onLogout}
      />

      <ScrollView
        contentContainerStyle={[styles.container, { backgroundColor: themeColors.secondary }]}
        keyboardShouldPersistTaps="handled"
      >
        {/* Mobile-First Action Buttons Row */}
        <View style={styles.actionButtonsContainer}>
          <View style={styles.actionButtonsRow}>
            {/* Speed Onboarding for Vegetable Shop */}
            {shop?.shopType === 'VEGETABLE_SHOP' && catalogs.length < 5 && (
              <TouchableOpacity
                onPress={handleSpeedOnboarding}
                style={[styles.mobileActionButton, { backgroundColor: accentColor }]}
                disabled={speedOnboarding}
              >
                <Text style={styles.mobileButtonText}>
                  {speedOnboarding ? 'Creating...' : 'Quick Catalogs'}
                </Text>
              </TouchableOpacity>
            )}

            {/* Speed Onboarding for Restaurant */}
            {shop?.shopType === 'RESTAURANT' && catalogs.length < 5 && (
              <TouchableOpacity
                onPress={handleSpeedOnboardingRestaurant}
                style={[styles.mobileActionButton, { backgroundColor: accentColor }]}
                disabled={speedOnboarding}
              >
                <Text style={styles.mobileButtonText}>
                  {speedOnboarding ? 'Creating...' : 'Quick Catalogs'}
                </Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              onPress={handleDownloadCSV}
              style={[styles.mobileActionButton, { backgroundColor: accentColor, opacity: catalogs.length === 0 ? 0.5 : 1 }]}
              disabled={catalogs.length === 0}
            >
              <Text style={styles.mobileButtonText}>Download Items</Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={handleDownloadTemplate}
              style={[styles.mobileActionButton, { backgroundColor: accentColor }]}
            >
              <Text style={styles.mobileButtonText}>Upload Templates</Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={handleUploadCSV}
              style={[styles.mobileActionButton, { backgroundColor: accentColor, opacity: uploadingCSV ? 0.5 : 1 }]}
              disabled={uploadingCSV}
            >
              <Text style={styles.mobileButtonText}>Bulk Upload</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* CSV Upload Progress */}
        {uploadingCSV && (
          <Card style={styles.progressCard}>
            <ActivityIndicator size="small" color={accentColor} />
            <Text variant="body" style={styles.progressText}>
              {csvProgress.message}
            </Text>
            {csvProgress.total > 0 && (
              <Text variant="caption" color="secondary">
                {csvProgress.current} / {csvProgress.total}
              </Text>
            )}
          </Card>
        )}

        {/* Speed Onboarding Progress */}
        {speedOnboarding && (
          <Card style={styles.progressCard}>
            <ActivityIndicator size="small" color={accentColor} />
            <Text variant="body" style={styles.progressText}>
              {onboardingProgress.message}
            </Text>
            {onboardingProgress.total > 0 && (
              <Text variant="caption" color="secondary">
                {onboardingProgress.current} / {onboardingProgress.total}
              </Text>
            )}
          </Card>
        )}

        {/* Mobile Filter Section with Add Button */}
        {/* Show filters if: (1) there are catalogs, OR (2) any filter is active */}
        {(catalogs.length > 0 || appliedNameFilter || appliedDescriptionFilter || selectedCategory) && (
          <View style={styles.mobileFilterSection}>

            {/* Category Filter */}
            <View style={styles.mobileFilterHeader}>
              <Text variant="body" bold style={styles.mobileFilterLabel}>
                Filter by Category:
              </Text>
              <TouchableOpacity
                onPress={handleAddNew}
                style={[styles.mobileAddButton, { backgroundColor: accentColor }]}
              >
                <Text style={styles.mobileAddButtonText}>+ Add New Item</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.categoryChipsContainer}>
              <TouchableOpacity
                onPress={() => setSelectedCategory('')}
                style={[
                  styles.categoryChip,
                  !selectedCategory && styles.categoryChipSelected,
                  { borderColor: accentColor, backgroundColor: !selectedCategory ? accentColor : 'transparent' }
                ]}
              >
                <Text
                  variant="caption"
                  bold
                  style={[
                    styles.categoryChipText,
                    { color: !selectedCategory ? '#FFFFFF' : accentColor }
                  ]}
                >
                  All
                </Text>
              </TouchableOpacity>
              {Array.from(new Set(catalogs.map(c => c.category).filter(Boolean))).sort().map((category) => (
                <TouchableOpacity
                  key={category}
                  onPress={() => setSelectedCategory(category)}
                  style={[
                    styles.categoryChip,
                    selectedCategory === category && styles.categoryChipSelected,
                    { borderColor: accentColor, backgroundColor: selectedCategory === category ? accentColor : 'transparent' }
                  ]}
                >
                  <Text
                    variant="caption"
                    bold
                    style={[
                      styles.categoryChipText,
                      { color: selectedCategory === category ? '#FFFFFF' : accentColor }
                    ]}
                  >
                    {category}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Advanced Filters - Collapsible - Moved below Category Filter */}
            <View style={styles.advancedFiltersContainer}>
              <TouchableOpacity
                onPress={() => setShowAdvancedFilters(!showAdvancedFilters)}
                style={styles.advancedFiltersHeader}
              >
                <Text variant="body" bold style={styles.filterSectionTitle}>
                  Advanced Filters {showAdvancedFilters ? '▼' : '▶'}
                </Text>
              </TouchableOpacity>

              {showAdvancedFilters && (
                <View style={styles.textFiltersContainer}>
                  <View style={styles.textFilterRow}>
                    <View style={styles.textFilterInput}>
                      <Text variant="caption" bold style={styles.textFilterLabel}>
                        Item Name
                      </Text>
                      <Input
                        placeholder="Search by name..."
                        value={nameFilter}
                        onChangeText={setNameFilter}
                      />
                    </View>
                    <View style={styles.textFilterInput}>
                      <Text variant="caption" bold style={styles.textFilterLabel}>
                        Description
                      </Text>
                      <Input
                        placeholder="Search by description..."
                        value={descriptionFilter}
                        onChangeText={setDescriptionFilter}
                      />
                    </View>
                  </View>
                  <View style={styles.filterActionsRow}>
                    <TouchableOpacity
                      onPress={handleApplyFilters}
                      style={[styles.filterButton, styles.applyButton, { backgroundColor: accentColor }]}
                      activeOpacity={0.7}
                    >
                      <Text style={styles.filterButtonTextWhite}>🔍 Apply Filters</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={handleResetFilters}
                      style={[styles.filterButton, styles.resetButton, { borderColor: accentColor }]}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.filterButtonText, { color: accentColor }]}>🔄 Reset All</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Mobile Add Button for Empty State */}
        {/* Only show if there are no catalogs AND no filters applied */}
        {catalogs.length === 0 && !appliedNameFilter && !appliedDescriptionFilter && !selectedCategory && (
          <View style={styles.mobileEmptyAddContainer}>
            <TouchableOpacity
              onPress={handleAddNew}
              style={[styles.mobileAddButton, { backgroundColor: accentColor }]}
            >
              <Text style={styles.mobileAddButtonText}>+ Add New Item</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Catalog List or Empty State */}
        {catalogs.length === 0 ? (
          <Card style={styles.emptyCard}>
            <Text variant="h3" bold style={styles.emptyTitle}>
              {appliedNameFilter || appliedDescriptionFilter || selectedCategory
                ? 'No Results Found'
                : 'No Catalog Items'}
            </Text>
            <Text variant="body" color="secondary" style={styles.emptyText}>
              {appliedNameFilter || appliedDescriptionFilter || selectedCategory
                ? 'No items match your search criteria. Try adjusting your filters or search terms.'
                : 'Add your first catalog item to get started'}
            </Text>
          </Card>
        ) : (
          <View style={styles.catalogList}>
            {catalogs.map((catalog) => (
              <Card key={catalog.id} style={styles.catalogCard}>
                <View style={styles.catalogContent}>
                  {/* Image with Lazy Loading - Clickable to view */}
                  {catalog.imageUrl && (
                    <TouchableOpacity
                      onPress={() =>
                        handleImageClick(
                          catalog.imageUrl || null,
                          catalog.additionalImages || []
                        )
                      }
                      activeOpacity={0.8}
                    >
                      <CatalogImage blobPath={catalog.imageUrl} style={styles.catalogImage} />
                    </TouchableOpacity>
                  )}

                  {/* Details */}
                  <View style={styles.catalogDetails}>
                    {/* Item Name with Share Icon */}
                    <View style={styles.catalogNameWithShare}>
                      <Text variant="body" bold style={styles.catalogName}>
                        {catalog.name}
                      </Text>
                      <TouchableOpacity
                        onPress={() => handleShare(catalog)}
                        style={styles.shareIconButton}
                        activeOpacity={0.7}
                      >
                        <Image
                          source={{ uri: '/assets/icons/share-icon.png' }}
                          style={styles.shareIcon}
                        />
                      </TouchableOpacity>
                    </View>
                    {catalog.description && (
                      <Text variant="caption" color="secondary" style={styles.catalogDescription}>
                        {catalog.description}
                      </Text>
                    )}
                    <View style={styles.catalogMeta}>
                      {catalog.category && (
                        <Text variant="caption" color="secondary">
                          Category: {catalog.category}
                        </Text>
                      )}
                      {catalog.price && (
                        <Text variant="caption" bold style={styles.catalogPrice}>
                          {catalog.price.currency} {catalog.price.value}{catalog.unit ? `/${catalog.unit}` : ''}
                          {catalog.price.discountPercentage && (
                            <Text variant="caption" color="secondary">
                              {' '}
                              ({catalog.price.discountPercentage}% off)
                            </Text>
                          )}
                        </Text>
                      )}
                    </View>
                    {catalog.availability && (
                      <Text
                        variant="caption"
                        style={[
                          styles.availabilityBadge,
                          {
                            color: catalog.availability.isAvailable ? colors.success : colors.error,
                          },
                        ]}
                      >
                        {catalog.availability.isAvailable ? '● Available' : '● Unavailable'}
                      </Text>
                    )}
                  </View>

                  {/* Actions */}
                  <View style={styles.catalogActions}>
                    <TouchableOpacity
                      onPress={() => handleEdit(catalog)}
                      style={[styles.actionIcon, { backgroundColor: accentColor }]}
                    >
                      <Text style={styles.actionIconText}>✏️</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => handleDelete(catalog)}
                      style={[styles.actionIcon, { backgroundColor: colors.error }]}
                    >
                      <Text style={styles.actionIconText}>🗑️</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </Card>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <View style={styles.loadMoreContainer}>
                <TouchableOpacity
                  onPress={handleLoadMore}
                  style={[styles.loadMoreButton, { backgroundColor: accentColor }]}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text style={styles.loadMoreButtonText}>Load More Items</Text>
                  )}
                </TouchableOpacity>
              </View>
            )}

            {/* End of list indicator */}
            {!hasMore && catalogs.length > 0 && (
              <View style={styles.endOfListContainer}>
                <Text variant="caption" color="secondary" style={styles.endOfListText}>
                  ✓ All items loaded
                </Text>
              </View>
            )}
          </View>
        )}
      </ScrollView>

      {/* Add/Edit Modal */}
      <Modal
        visible={showAddEdit}
        transparent
        animationType="slide"
        onRequestClose={() => setShowAddEdit(false)}
      >
        <View style={[styles.modalContainer, { backgroundColor: 'rgba(0, 0, 0, 0.5)' }]}>
          <ScrollView
            contentContainerStyle={[styles.modalContent, { backgroundColor: themeColors.secondary }]}
            keyboardShouldPersistTaps="handled"
          >
            <View style={[styles.modalHeader, { backgroundColor: themeColors.primary }]}>
              <Text variant="h3" bold style={{ color: colors.white }}>
                {editingCatalog ? '✏️ Edit Item' : '➕ Add New Item'}
              </Text>
              <TouchableOpacity onPress={() => setShowAddEdit(false)}>
                <Text style={[styles.closeButton, { color: colors.white }]}>✕</Text>
              </TouchableOpacity>
            </View>

            <Card style={styles.formCard}>
              <Input
                label="Name *"
                placeholder="e.g. Margherita Pizza"
                value={form.name}
                onChangeText={(value) => {
                  setForm((prev) => ({ ...prev, name: value }));
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                }}
                error={errors.name}
              />

              <Input
                label="Description"
                placeholder="Brief description"
                value={form.description}
                onChangeText={(value) => setForm((prev) => ({ ...prev, description: value }))}
                multiline
                numberOfLines={3}
              />

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Category *</Text>

                {/* Toggle Switch for Select/Custom */}
                <View style={styles.toggleContainer}>
                  <Text style={[
                    styles.toggleLabel,
                    categoryInputMode === 'dropdown' && styles.toggleLabelActive
                  ]}>Select</Text>

                  <TouchableOpacity
                    style={styles.toggleSwitch}
                    onPress={() => setCategoryInputMode(categoryInputMode === 'dropdown' ? 'custom' : 'dropdown')}
                  >
                    <View style={[
                      styles.toggleTrack,
                      categoryInputMode === 'custom' && styles.toggleTrackActive
                    ]}>
                      <View style={[
                        styles.toggleThumb,
                        categoryInputMode === 'custom' && styles.toggleThumbActive
                      ]} />
                    </View>
                  </TouchableOpacity>

                  <Text style={[
                    styles.toggleLabel,
                    categoryInputMode === 'custom' && styles.toggleLabelActive
                  ]}>Custom</Text>
                </View>

                {categoryInputMode === 'dropdown' ? (
                  // Dropdown Mode - Same as Unit field
                  <View style={styles.pickerContainer}>
                    <select
                      value={form.category}
                      onChange={(e) => {
                        console.log('🔄 [Catalog] Category selected:', e.target.value);
                        setForm((prev) => ({ ...prev, category: e.target.value }));
                        if (errors.category) setErrors((prev) => ({ ...prev, category: undefined }));
                      }}
                      style={styles.picker}
                    >
                      <option value="">Select Category</option>
                      {Array.isArray(categories) && categories.length > 0 &&
                        categories.map((category) => (
                          <option key={category.id} value={category.categoryName}>
                            {category.categoryName}
                          </option>
                        ))
                      }
                    </select>
                  </View>
                ) : (
                  // Custom Text Mode
                  <Input
                    placeholder="Enter custom category name"
                    value={form.category}
                    onChangeText={(value) => {
                      setForm((prev) => ({ ...prev, category: value }));
                      if (errors.category) setErrors((prev) => ({ ...prev, category: undefined }));
                    }}
                  />
                )}

                {loadingConfig && (
                  <Text variant="caption" color="secondary" style={styles.helperText}>
                    Loading category suggestions...
                  </Text>
                )}
                {errors.category && (
                  <Text variant="caption" color="error" style={styles.helperText}>
                    {errors.category}
                  </Text>
                )}
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Unit *</Text>
                <View style={styles.pickerContainer}>
                  <select
                    value={form.unit}
                    onChange={(e) => {
                      console.log('🔄 [Catalog] Unit changed:', e.target.value);
                      setForm((prev) => ({ ...prev, unit: e.target.value }));
                      if (errors.unit) setErrors((prev) => ({ ...prev, unit: undefined }));
                    }}
                    style={styles.picker}
                  >
                    <option value="">Select Unit</option>
                    {Array.isArray(uoms) && uoms.length > 0 ? (
                      uoms.map((uom) => {
                        console.log('📏 [Catalog] Rendering UOM option:', uom);
                        return (
                          <option key={uom.code} value={uom.displayName}>
                            {uom.displayName} ({uom.name})
                          </option>
                        );
                      })
                    ) : (
                      <option value="" disabled>Loading units...</option>
                    )}
                  </select>
                </View>
                {loadingConfig && (
                  <Text variant="caption" color="secondary" style={styles.helperText}>
                    Loading units...
                  </Text>
                )}
                {!loadingConfig && (!uoms || uoms.length === 0) && (
                  <Text variant="caption" color="error" style={styles.helperText}>
                    No units available
                  </Text>
                )}
                {errors.unit && (
                  <Text variant="caption" color="error" style={styles.helperText}>
                    {errors.unit}
                  </Text>
                )}
              </View>

              <View style={styles.priceGroup}>
                <View style={styles.priceInput}>
                  <Input
                    label="Price *"
                    placeholder="0.00"
                    value={form.price}
                    onChangeText={(value) => {
                      setForm((prev) => ({ ...prev, price: value }));
                      if (errors.price) setErrors((prev) => ({ ...prev, price: undefined }));
                    }}
                    keyboardType="decimal-pad"
                    error={errors.price}
                  />
                </View>
                <View style={styles.currencyInput}>
                  <Text style={styles.label}>Currency *</Text>
                  <View style={styles.pickerContainer}>
                    <select
                      value={form.currency}
                      onChange={(e) => {
                        console.log('🔄 [Catalog] Currency changed:', e.target.value);
                        setForm((prev) => ({ ...prev, currency: e.target.value }));
                        if (errors.currency) setErrors((prev) => ({ ...prev, currency: undefined }));
                      }}
                      style={styles.picker}
                    >
                      <option value="">Select Currency</option>
                      {Array.isArray(countries) && countries.length > 0 ? (
                        countries.map((country) => {
                          console.log('💰 [Catalog] Rendering currency option:', country);
                          return (
                            <option key={country.id} value={country.currency}>
                              {country.currency} ({country.currencySymbol}) - {country.countryName}
                            </option>
                          );
                        })
                      ) : (
                        <option value="" disabled>Loading currencies...</option>
                      )}
                    </select>
                  </View>
                  {loadingConfig && (
                    <Text variant="caption" color="secondary" style={styles.helperText}>
                      Loading currencies...
                    </Text>
                  )}
                  {!loadingConfig && (!countries || countries.length === 0) && (
                    <Text variant="caption" color="error" style={styles.helperText}>
                      No currencies available
                    </Text>
                  )}
                  {errors.currency && (
                    <Text variant="caption" color="error" style={styles.helperText}>
                      {errors.currency}
                    </Text>
                  )}
                </View>
              </View>

              <Input
                label="Discount %"
                placeholder="Optional"
                value={form.discountPercentage}
                onChangeText={(value) =>
                  setForm((prev) => ({ ...prev, discountPercentage: value }))
                }
                keyboardType="decimal-pad"
              />

              <Input
                label="Stock"
                placeholder="Optional"
                value={form.stock}
                onChangeText={(value) => setForm((prev) => ({ ...prev, stock: value }))}
                keyboardType="number-pad"
              />

              {/* Image Upload */}
              <View style={styles.imageSection}>
                <Text style={styles.label}>Product Image</Text>
                <Text style={styles.caption}>
                  Max 5MB • JPG, PNG, WEBP formats
                </Text>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />

                {form.imagePreviewUrl ? (
                  <View style={styles.imagePreview}>
                    {/* Image Preview - Clickable */}
                    <TouchableOpacity
                      onPress={() => {
                        const additionalBlobs = form.additionalImageBlobs.filter(
                          (blob): blob is string => blob !== null
                        );
                        handleImageClick(form.blobPath, additionalBlobs);
                      }}
                      activeOpacity={0.8}
                    >
                      <div style={{
                        width: 200,
                        height: 200,
                        borderRadius: 8,
                        border: '1px solid #ddd',
                        marginBottom: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        backgroundColor: '#f5f5f5',
                        cursor: 'pointer',
                      }}>
                        <img
                          src={form.imagePreviewUrl}
                          alt="Product preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'cover',
                            borderRadius: 8
                          }}
                          onLoad={() => console.log('✅ [Catalog] Preview loaded')}
                          onError={(e) => {
                            console.error('❌ [Catalog] Preview error:', e);
                          }}
                        />
                      </div>
                    </TouchableOpacity>

                    {/* Upload Progress */}
                    {uploadingImage && uploadProgress > 0 && uploadProgress < 100 && (
                      <View style={styles.uploadProgress}>
                        <Text style={styles.uploadProgressText}>
                          Uploading: {uploadProgress}%
                        </Text>
                        <View style={styles.progressBar}>
                          <View
                            style={[
                              styles.progressFill,
                              { width: `${uploadProgress}%` }
                            ]}
                          />
                        </View>
                      </View>
                    )}

                    {/* Image Actions */}
                    <View style={styles.imageActions}>
                      <Button
                        variant="outline"
                        onPress={handleBrowseClick}
                        disabled={uploadingImage}
                        style={styles.imageButton}
                      >
                        Change Image
                      </Button>
                      <Button
                        variant="outline"
                        onPress={handleRemoveImage}
                        disabled={uploadingImage}
                        style={styles.imageButton}
                      >
                        Remove
                      </Button>
                    </View>
                  </View>
                ) : (
                  <Button
                    variant="outline"
                    onPress={handleBrowseClick}
                    disabled={uploadingImage}
                    loading={uploadingImage}
                    themeColor={accentColor}
                  >
                    {uploadingImage ? 'Uploading...' : '📷 Browse Image'}
                  </Button>
                )}

                {/* Debug Info */}
                {form.blobPath && (
                  <Text variant="caption" color="secondary" style={styles.debugText}>
                    Blob Path: {form.blobPath.substring(0, 50)}...
                  </Text>
                )}
              </View>

              {/* Additional Images Upload (Up to 5) */}
              <View style={styles.imageSection}>
                <Text style={styles.label}>Additional Images</Text>
                <Text style={styles.caption}>
                  Add up to 5 additional product images (Max 5MB each • JPG, PNG, WEBP)
                </Text>

                {/* Hidden file inputs for 5 additional images */}
                {[0, 1, 2, 3, 4].map((index) => (
                  <input
                    key={`additional-input-${index}`}
                    ref={(el) => (additionalFileInputRefs.current[index] = el)}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Validate file
                        if (file.size > 5 * 1024 * 1024) {
                          Alert.alert('File Too Large', 'Image must be less than 5MB');
                          return;
                        }
                        if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
                          Alert.alert('Invalid Format', 'Please select a JPG, PNG, or WEBP image');
                          return;
                        }

                        console.log(`✅ [Catalog] Additional image ${index + 1} selected:`, file.name);

                        // Update selected files
                        const newFiles = [...selectedAdditionalFiles];
                        newFiles[index] = file;
                        setSelectedAdditionalFiles(newFiles);

                        // Create preview URL
                        const previewUrl = URL.createObjectURL(file);
                        setForm((prev) => {
                          const newPreviews = [...prev.additionalImagePreviews];
                          newPreviews[index] = previewUrl;
                          return {
                            ...prev,
                            additionalImagePreviews: newPreviews,
                          };
                        });
                      }
                    }}
                  />
                ))}

                {/* Grid of additional image slots */}
                <View style={styles.additionalImagesGrid}>
                  {[0, 1, 2, 3, 4].map((index) => (
                    <View key={`additional-${index}`} style={styles.additionalImageSlot}>
                      {form.additionalImagePreviews[index] || form.additionalImageBlobs[index] ? (
                        <View style={styles.additionalImagePreview}>
                          <div
                            style={{
                              width: 100,
                              height: 100,
                              borderRadius: 8,
                              border: '1px solid #ddd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              backgroundColor: '#f5f5f5',
                              position: 'relative',
                            }}
                          >
                            {form.additionalImagePreviews[index] ? (
                              <img
                                src={form.additionalImagePreviews[index]!}
                                alt={`Additional ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: 8,
                                }}
                              />
                            ) : (
                              <CatalogImage
                                blobPath={form.additionalImageBlobs[index]}
                                style={{
                                  width: 100,
                                  height: 100,
                                  borderRadius: 8,
                                }}
                              />
                            )}
                          </div>
                          <TouchableOpacity
                            onPress={() => handleRemoveAdditionalImage(index)}
                            style={styles.removeAdditionalImageButton}
                          >
                            <Text style={styles.removeAdditionalImageText}>✕</Text>
                          </TouchableOpacity>
                        </View>
                      ) : (
                        <TouchableOpacity
                          onPress={() => handleAdditionalImageBrowseClick(index)}
                          style={styles.additionalImagePlaceholder}
                        >
                          <Text style={styles.additionalImagePlaceholderText}>
                            📷 +{'\n'}Image {index + 1}
                          </Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  ))}
                </View>
              </View>

              {/* Availability Toggle */}
              <View style={styles.availabilitySection}>
                <Text style={styles.label}>Availability</Text>
                <TouchableOpacity
                  onPress={() =>
                    setForm((prev) => ({ ...prev, isAvailable: !prev.isAvailable }))
                  }
                  style={styles.toggleButton}
                >
                  <View
                    style={[
                      styles.toggle,
                      {
                        backgroundColor: form.isAvailable
                          ? accentColor
                          : colors.gray3,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.toggleKnob,
                        form.isAvailable && styles.toggleKnobActive,
                      ]}
                    />
                  </View>
                  <Text variant="body" style={styles.toggleLabel}>
                    {form.isAvailable ? 'Available' : 'Unavailable'}
                  </Text>
                </TouchableOpacity>
              </View>

              <View style={styles.modalActions}>
                <Button
                  variant="secondary"
                  onPress={() => setShowAddEdit(false)}
                  disabled={saving}
                  style={styles.modalActionButton}
                >
                  Cancel
                </Button>
                <Button
                  onPress={handleSave}
                  disabled={saving}
                  loading={saving}
                  style={styles.modalActionButton}
                  themeColor={accentColor}
                >
                  {saving ? 'Saving...' : editingCatalog ? 'Update' : 'Create'}
                </Button>
              </View>
            </Card>
          </ScrollView>
        </View>
      </Modal>

      {/* Image Viewing Modal */}
      <Modal
        visible={showImageModal}
        transparent
        animationType="fade"
        onRequestClose={handleCloseImageModal}
      >
        <View style={styles.imageModalContainer}>
          <TouchableOpacity
            style={styles.imageModalClose}
            onPress={handleCloseImageModal}
            activeOpacity={0.9}
          >
            <Text style={styles.imageModalCloseText}>✕</Text>
          </TouchableOpacity>

          <View style={styles.imageModalContent}>
            {viewingImages && getCurrentImage() && (
              <>
                <CatalogImage
                  blobPath={getCurrentImage()}
                  style={styles.imageModalImage}
                />

                {/* Image counter if multiple images */}
                {viewingImages.additional.length > 0 && (
                  <View style={styles.imageModalCounter}>
                    <Text style={styles.imageModalCounterText}>
                      {currentImageIndex + 1} / {1 + viewingImages.additional.length}
                    </Text>
                  </View>
                )}

                {/* Navigation buttons if multiple images */}
                {viewingImages.additional.length > 0 && (
                  <>
                    <TouchableOpacity
                      style={[styles.imageModalNav, styles.imageModalNavPrev]}
                      onPress={handlePrevImage}
                      activeOpacity={0.7}
                    >
                      <Text style={styles.imageModalNavText}>‹</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.imageModalNav, styles.imageModalNavNext]}
                      onPress={handleNextImage}
                      activeOpacity={0.7}
                    >
                      <Text style={styles.imageModalNavText}>›</Text>
                    </TouchableOpacity>
                  </>
                )}

                {/* Thumbnail strip */}
                {viewingImages.additional.length > 0 && (
                  <View style={styles.imageModalThumbnails}>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                      {/* Main image thumbnail */}
                      <TouchableOpacity
                        onPress={() => setCurrentImageIndex(0)}
                        style={[
                          styles.imageModalThumbnail,
                          currentImageIndex === 0 && styles.imageModalThumbnailActive,
                        ]}
                      >
                        <CatalogImage
                          blobPath={viewingImages.main}
                          style={styles.imageModalThumbnailImage}
                        />
                      </TouchableOpacity>

                      {/* Additional image thumbnails */}
                      {viewingImages.additional.map((img, idx) => (
                        <TouchableOpacity
                          key={`thumb-${idx}`}
                          onPress={() => setCurrentImageIndex(idx + 1)}
                          style={[
                            styles.imageModalThumbnail,
                            currentImageIndex === idx + 1 && styles.imageModalThumbnailActive,
                          ]}
                        >
                          <CatalogImage
                            blobPath={img}
                            style={styles.imageModalThumbnailImage}
                          />
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                )}
              </>
            )}
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingVertical: 0,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
  },
  loadingSubtext: {
    marginTop: 8,
    fontSize: 12,
    opacity: 0.7,
  },
  // Mobile-First Action Buttons
  actionButtonsContainer: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  actionButtonsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 4,
  },
  mobileActionButton: {
    flex: 1,
    minHeight: 36,
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    position: 'relative',
  },
  mobileButtonText: {
    fontSize: 10,
    fontWeight: '600',
    color: colors.white,
    textAlign: 'center',
    lineHeight: 12,
  },
  mobileInfoButton: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Mobile Filter Section
  mobileFilterSection: {
    marginHorizontal: 12,
    marginVertical: 8,
    padding: 12,
    backgroundColor: colors.white,
    borderRadius: 8,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  mobileFilterHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  mobileFilterLabel: {
    fontSize: 14,
    color: colors.gray9,
    flex: 1,
  },
  mobileAddButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  mobileAddButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.white,
  },
  mobileEmptyAddContainer: {
    paddingHorizontal: 12,
    marginBottom: 16,
    alignItems: 'flex-end',
  },
  // Filter and Add Button Section
  filterHeaderSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 16,
    backgroundColor: colors.white,
    borderRadius: 12,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  filterContent: {
    flex: 1,
  },
  addNewButton: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 140,
    alignSelf: 'flex-start',
  },
  emptyAddButtonContainer: {
    paddingHorizontal: 16,
    marginBottom: 16,
    alignItems: 'flex-end',
  },
  // Text Filters - moved to Advanced Filters section
  filterSectionTitle: {
    fontSize: 14,
    color: colors.gray9,
    marginBottom: 12,
  },
  textFilterRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  textFilterInput: {
    flex: 1,
  },
  filterActionsRow: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-end',
  },
  filterButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 6,
    minWidth: 120,
    alignItems: 'center',
  },
  applyButton: {
    // backgroundColor set dynamically
  },
  resetButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
  },
  filterButtonTextWhite: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.white,
  },
  filterButtonText: {
    fontSize: 13,
    fontWeight: '600',
  },
  // Pagination
  loadMoreContainer: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  loadMoreButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    minWidth: 200,
    alignItems: 'center',
  },
  loadMoreButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
  },
  endOfListContainer: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  endOfListText: {
    fontSize: 12,
  },
  catalogList: {
    paddingHorizontal: 12,
    marginBottom: 24,
  },
  catalogCard: {
    marginBottom: 12,
  },
  catalogContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  catalogImage: {
    width: 64,
    height: 64,
    borderRadius: 6,
    marginRight: 12,
    backgroundColor: colors.gray2,
  },
  catalogDetails: {
    flex: 1,
  },
  catalogNameWithShare: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  shareIconButton: {
    padding: 8,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
  shareIcon: {
    width: 20,
    height: 20,
    resizeMode: 'contain',
  },
  catalogName: {
    marginBottom: 0,
    flex: 1,
  },
  catalogDescription: {
    marginBottom: 8,
  },
  catalogMeta: {
    marginBottom: 4,
  },
  catalogPrice: {
    marginTop: 4,
    color: colors.primary,
  },
  availabilityBadge: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: '600',
  },
  catalogActions: {
    flexDirection: 'column',
    gap: 8,
  },
  actionIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionIconText: {
    fontSize: 16,
  },
  emptyCard: {
    padding: 24,
    alignItems: 'center',
    marginHorizontal: 12,
  },
  emptyTitle: {
    marginBottom: 8,
  },
  emptyText: {
    textAlign: 'center',
  },
  modalContainer: {
    flex: 1,
  },
  modalContent: {
    flexGrow: 1,
    marginTop: 60,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 16,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    marginHorizontal: -16,
    marginTop: -16,
    marginBottom: 24,
  },
  closeButton: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  formCard: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray9,
    marginBottom: 8,
  },
  priceGroup: {
    flexDirection: 'row',
    gap: 12,
  },
  priceInput: {
    flex: 2,
  },
  currencyInput: {
    flex: 1,
  },
  imageSection: {
    marginBottom: 16,
  },
  imagePreview: {
    alignItems: 'center',
    marginTop: 8,
  },
  previewImage: {
    width: 200,
    height: 200,
    borderRadius: 8,
    marginBottom: 12,
    backgroundColor: colors.gray2,
    borderWidth: 1,
    borderColor: colors.gray3,
  },
  imageActions: {
    flexDirection: 'row',
    gap: 8,
  },
  imageButton: {
    paddingHorizontal: 16,
  },
  debugText: {
    marginTop: 4,
    fontSize: 10,
    textAlign: 'center',
  },
  availabilitySection: {
    marginBottom: 16,
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  toggle: {
    width: 50,
    height: 28,
    borderRadius: 14,
    padding: 2,
    marginRight: 12,
  },
  toggleKnob: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.white,
  },
  toggleKnobActive: {
    marginLeft: 'auto',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalActionButton: {
    flex: 1,
  },
  // Category dropdown styles
  inputGroup: {
    marginBottom: 16,
    width: '100%',
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: colors.gray3,
    borderRadius: 8,
    backgroundColor: colors.white,
    minHeight: 48,
    justifyContent: 'center',
    position: 'relative',
  },
  picker: {
    width: '100%',
    height: 48,
    paddingHorizontal: 12,
    paddingVertical: 0,
    fontSize: 14,
    color: colors.gray9,
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderRadius: 8,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    textAlign: 'left',
  } as any,
  helperText: {
    marginTop: 6,
    marginBottom: 0,
    fontSize: 12,
    lineHeight: 16,
    paddingLeft: 2,
  },
  progressCard: {
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 16,
    alignItems: 'center',
    gap: 8,
  },
  progressText: {
    textAlign: 'center',
    marginTop: 8,
  },
  // Category Filter styles
  filterLabel: {
    marginBottom: 12,
    color: colors.gray9,
  },
  categoryChipsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  categoryChip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 2,
    marginBottom: 4,
  },
  categoryChipSelected: {
    // backgroundColor set dynamically
  },
  categoryChipText: {
    fontSize: 14,
    // color set dynamically
  },
  // Category toggle switch styles
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 12,
  },
  toggleLabel: {
    fontSize: 14,
    color: colors.gray6,
    fontWeight: '500',
    minWidth: 50,
  },
  toggleLabelActive: {
    color: colors.primary,
    fontWeight: '600',
  },
  toggleSwitch: {
    padding: 2,
  },
  toggleTrack: {
    width: 50,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.gray3,
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  toggleTrackActive: {
    backgroundColor: colors.primary,
  },
  toggleThumb: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.white,
    alignSelf: 'flex-start',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 3,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  // Advanced Filters styles
  advancedFiltersContainer: {
    marginTop: 16,
  },
  advancedFiltersHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    marginBottom: 4,
  },
  advancedFiltersToggle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.gray7,
  },
  // Text Filter styles
  textFiltersContainer: {
    marginTop: 8,
    padding: 16,
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
  },
  textFilterLabel: {
    marginBottom: 4,
    color: colors.gray8,
  },
  // Additional Images styles
  additionalImagesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 12,
  },
  additionalImageSlot: {
    width: 100,
    height: 100,
  },
  additionalImagePreview: {
    position: 'relative',
    width: 100,
    height: 100,
  },
  removeAdditionalImageButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.error,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  removeAdditionalImageText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: 'bold',
  },
  additionalImagePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 8,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: colors.gray4,
    backgroundColor: colors.gray1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  additionalImagePlaceholderText: {
    fontSize: 12,
    color: colors.gray6,
    textAlign: 'center',
  },
  // Image Modal styles
  imageModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageModalClose: {
    position: 'absolute',
    top: 40,
    right: 20,
    zIndex: 10,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageModalCloseText: {
    color: colors.white,
    fontSize: 28,
    fontWeight: 'bold',
  },
  imageModalContent: {
    width: '90%',
    maxWidth: 800,
    maxHeight: '80%',
    position: 'relative',
  },
  imageModalImage: {
    width: '100%',
    height: 600,
    borderRadius: 8,
  },
  imageModalCounter: {
    position: 'absolute',
    top: 16,
    left: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  imageModalCounterText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '600',
  },
  imageModalNav: {
    position: 'absolute',
    top: '50%',
    marginTop: -30,
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageModalNavPrev: {
    left: -80,
  },
  imageModalNavNext: {
    right: -80,
  },
  imageModalNavText: {
    color: colors.white,
    fontSize: 48,
    fontWeight: '300',
    lineHeight: 60,
  },
  imageModalThumbnails: {
    marginTop: 16,
    paddingVertical: 8,
  },
  imageModalThumbnail: {
    width: 80,
    height: 80,
    marginRight: 8,
    borderRadius: 6,
    borderWidth: 3,
    borderColor: 'transparent',
    overflow: 'hidden',
  },
  imageModalThumbnailActive: {
    borderColor: colors.white,
  },
  imageModalThumbnailImage: {
    width: '100%',
    height: '100%',
  },
});

